import { BrushReveal } from "../fate-zero-vote/brush-reveal.es6";

export default class Slider {
    constructor() {
        this.$root = document.querySelector(".js-chara-slider");
        if (!this.$root) return;

        this.$slides = Array.from(this.$root.querySelectorAll(".js-chara-slide"));
        this.$bodies = Array.from(this.$root.querySelectorAll(".js-chara-body"));
        this.$buttons = Array.from(this.$root.querySelectorAll(".js-chara-select"));
        this.$changes = Array.from(this.$root.querySelectorAll(".js-chara-change"));
        this.$layers = Array.from(this.$root.querySelectorAll(".character__ph__img"));
        this.reveals = new Map();
        this.lockTimers = new Map();
        this.changeSeq = 0;
        this.$profile = this.$root.querySelector(".character__profile");
        this.$arrows = Array.from(this.$root.querySelectorAll(".character__arw"));

        this.bindChange();
        this.schedulePreload();

        if (this.$slides.length < 2) return;

        this.current = this.$slides.findIndex(($slide) => $slide.classList.contains("-current"));
        if (this.current < 0) this.current = 0;

        this.bind();
    }

    bind() {
        const $prev = this.$root.querySelector(".js-chara-prev");
        const $next = this.$root.querySelector(".js-chara-next");

        if ($prev) $prev.addEventListener("click", () => this.move(-1));
        if ($next) $next.addEventListener("click", () => this.move(1));

        this.$buttons.forEach(($button) => {
            $button.addEventListener("click", () => {
                const index = this.$slides.findIndex(($slide) => $slide.dataset.chara === $button.dataset.chara);
                if (index >= 0) this.go(index);
            });
        });

        this.bindSwipe();
    }

    bindChange() {
        this.$changes.forEach(($button) => {
            $button.addEventListener("click", () => {
                const $slide = $button.closest(".js-chara-slide");
                if (!$slide || $button.classList.contains("-locked")) return;

                this.lockChange($button);
                this.loadChange($slide);

                const isChanged = !$slide.classList.contains("-changed");
                $button.setAttribute("aria-pressed", isChanged ? "true" : "false");
                this.revealPh($slide, isChanged);
            });
        });
    }

    lockChange($button) {
        $button.classList.add("-locked");
        $button.setAttribute("aria-disabled", "true");
        clearTimeout(this.lockTimers.get($button));
        this.lockTimers.set(
            $button,
            setTimeout(() => this.unlockChange($button), 1200)
        );
    }

    unlockChange($button) {
        clearTimeout(this.lockTimers.get($button));
        this.lockTimers.delete($button);
        $button.classList.remove("-locked");
        $button.removeAttribute("aria-disabled");
    }

    schedulePreload() {
        const preload = () => {
            setTimeout(() => {
                const $current = this.$slides.find(($slide) => $slide.classList.contains("-current")) || this.$slides[0];
                if ($current) this.loadChange($current);
            }, 2000);
        };

        if (document.readyState === "complete") {
            preload();
        } else {
            window.addEventListener("load", preload, { once: true });
        }
    }

    loadChange($slide) {
        $slide.querySelectorAll("[data-srcset]").forEach(($source) => {
            $source.srcset = $source.dataset.srcset;
            $source.removeAttribute("data-srcset");
        });

        $slide.querySelectorAll("[data-src]").forEach(($image) => {
            $image.src = $image.dataset.src;
            $image.removeAttribute("data-src");
            this.decodeImage($image);
        });
    }

    decodeImage($image) {
        if (typeof $image.decode !== "function") return Promise.resolve();

        return $image.decode().catch(() => {});
    }

    stopReveal($layer) {
        const $canvas = $layer.querySelector(".js-chara-canvas");
        const running = $canvas && this.reveals.get($canvas);

        if (running) {
            running.stop();
            this.reveals.delete($canvas);
        }

        $layer.classList.remove("-revealing");
    }

    revealPh($slide, isChanged) {
        Array.from($slide.querySelectorAll(".character__ph__img")).forEach(($layer) => this.stopReveal($layer));

        const seq = ++this.changeSeq;
        const $layer = $slide.querySelector(isChanged ? ".character__ph__img.-change" : ".character__ph__img.-default");
        const $canvas = $layer && $layer.querySelector(".js-chara-canvas");
        const $image = $layer && $layer.querySelector("img");

        if (!$canvas || !$image) {
            $slide.classList.toggle("-changed", isChanged);
            return;
        }

        this.decodeImage($image).then(() => {
            if (seq !== this.changeSeq) return;

            const reveal = new BrushReveal($canvas, {
                width: $image.naturalWidth || 790,
                height: $image.naturalHeight || 1868,
                delay: 0,
                gradientOpacity: 0,
            });
            reveal.img = $image;

            this.reveals.set($canvas, reveal);
            $layer.classList.add("-revealing");
            $slide.classList.toggle("-changed", isChanged);

            const finish = () => {
                if (this.reveals.get($canvas) !== reveal) return;

                $layer.classList.remove("-revealing");
                this.reveals.delete($canvas);
            };

            reveal.play().then(finish, finish);
        });
    }

    resetChange() {
        this.changeSeq += 1;
        this.$slides.forEach(($slide) => $slide.classList.remove("-changed"));
        this.$changes.forEach(($button) => {
            $button.setAttribute("aria-pressed", "false");
            this.unlockChange($button);
        });
        this.$layers.forEach(($layer) => this.stopReveal($layer));
    }

    bindSwipe() {
        let startX = 0;
        let startY = 0;
        let tracking = false;

        this.$root.addEventListener(
            "touchstart",
            (e) => {
                if (e.touches.length !== 1) return;

                startX = e.touches[0].clientX;
                startY = e.touches[0].clientY;
                tracking = true;
            },
            { passive: true }
        );

        //縦方向の動きが勝ったら、ページのスクロール操作とみなして追跡をやめる。
        this.$root.addEventListener(
            "touchmove",
            (e) => {
                if (!tracking) return;

                const dx = e.touches[0].clientX - startX;
                const dy = e.touches[0].clientY - startY;

                if (Math.abs(dy) > Math.abs(dx)) tracking = false;
            },
            { passive: true }
        );

        this.$root.addEventListener(
            "touchend",
            (e) => {
                if (!tracking) return;
                tracking = false;

                const dx = e.changedTouches[0].clientX - startX;
                const threshold = Math.max(40, window.innerWidth * 0.12);

                if (Math.abs(dx) < threshold) return;

                this.move(dx < 0 ? 1 : -1);
            },
            { passive: true }
        );
    }

    move(step) {
        const length = this.$slides.length;
        this.go((this.current + step + length) % length);
    }

    go(index) {
        if (index === this.current) return;

        this.current = index;
        this.$root.classList.add("-switched");
        this.resetChange();

        const chara = this.$slides[index].dataset.chara;

        this.$slides.forEach(($slide) => this.setState($slide, $slide.dataset.chara === chara));
        this.loadChange(this.$slides[index]);
        this.$bodies.forEach(($body) => this.setState($body, $body.dataset.chara === chara));
        this.replay(this.$profile);
        this.$arrows.forEach(($arrow) => this.replay($arrow));

        this.scrollToStage();

        this.$buttons.forEach(($button) => {
            if ($button.dataset.chara === chara) {
                $button.setAttribute("aria-current", "true");
            } else {
                $button.removeAttribute("aria-current");
            }
        });
    }

    //切り替え後のキャラが画面外だと何が変わったか分からないため、ビジュアルの位置まで戻す。
    scrollToStage() {
        if (this.$root.getBoundingClientRect().top >= 0) return;

        if (window.lenis) {
            window.lenis.scrollTo(this.$root, {
                duration: 0.45,
                easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
            });
            return;
        }

        this.$root.scrollIntoView({ behavior: "smooth", block: "start" });
    }

    replay($target) {
        if (!$target) return;

        $target.style.animation = "none";
        void $target.offsetWidth;
        $target.style.animation = "";
    }

    setState($target, isCurrent) {
        $target.classList.toggle("-current", isCurrent);

        if (isCurrent) {
            $target.removeAttribute("aria-hidden");
        } else {
            $target.setAttribute("aria-hidden", "true");
        }
    }
}
